import { useParams } from 'react-router-dom'
import { gql } from 'graphql-request'
import Breadcrumbs from '@components/Breadcrumbs'
import { useNavigate } from 'react-router-dom'

import { useQuery } from '@hooks/graphql'
import { useCurrentUser } from '@contexts/currentUser'
import useResearchConsent from '../../hooks/useResearchConsent'

import Lesson from './Lesson'
import CourseHeader from './CourseHeader'

const SERIES_QUERY = gql`
  query series($id: ID!) {
    node(id: $id) {
      ... on Series {
        id
        name
        description
        educatorName
        organizationName
        coverImage {
          url
        }
        assignment {
          id
          closed
          startDate
          dueDate
          currentTime
          classroom {
            id
            name
          }
        }
        educatorProjects {
          id
          name
          totalPossibleScore
          mySubmission {
            id
            status
            score
          }
        }
      }
    }
  }
`

const CourseDetails = ({ pageHeading }) => {
  const navigate = useNavigate()

  const { classroomId, id } = useParams()
  const { currentMembership: { organization: { features } } } = useCurrentUser()
  const isCourseInsightsEnabled = features.includes('course_insights')

  const { isLoading, data: { node: series = {} } = {} } = useQuery({
    queryKey: ['series', id],
    gqlQuery: SERIES_QUERY,
    variables: { id }
  })

  const classroom = series?.assignment?.classroom || { id: null }

  const { isLoading: isCheckingResearchConsent } = useResearchConsent(classroom.id)

  if (isLoading || isCheckingResearchConsent) return

  if (series?.assignment?.startDate && (Date.parse(series?.assignment?.currentTime) < Date.parse(series?.assignment?.startDate))) {
    navigate(`/students/classrooms/${classroom.id}/courses`)
  }

  const nextProject = series.educatorProjects?.find(project => project.mySubmission?.status !== 'COMPLETED' && project.mySubmission?.status !== 'RETURNED')
  const nextProjectId = nextProject?.id

  const isAssignmentClosed = series?.assignment?.closed

  return (
    <>
      <Breadcrumbs>
        <Breadcrumbs.Link label='Classrooms' to='/students/classrooms' />
        <Breadcrumbs.Link label={classroom.name} to={`/students/classrooms/${classroom.id}/courses`} />
        <Breadcrumbs.Text label={series.name} />
      </Breadcrumbs>

      <div className='w-full'>
        <div className='max-w-3xl mx-auto mt-8 pb-8'>
          <div className='px-4 sm:px-0 mb-6'>
            <CourseHeader
              courseId={id}
              name={series.name}
              description={series.description}
              imageUrl={series.coverImage?.url}
              startDate={series?.assignment?.startDate}
              dueDate={series?.assignment?.dueDate}
              isClosed={isAssignmentClosed}
            />
          </div>

          <div className='flex flex-col gap-5 w-full px-4 sm:px-0'>
            <div className='flex flex-col w-full gap-3 mx-auto'>
              <h3 className='text-lg text-primary font-semibold ml-3 sm:ml-0'>Lessons</h3>

              <For index='index' each='project' of={series.educatorProjects}>
                <Lesson
                  key={project.id}
                  id={project.id}
                  courseId={id}
                  name={project.name}
                  status={project.mySubmission?.status}
                  isNextProject={project.id === nextProjectId}
                  isAssignmentClosed={isAssignmentClosed}
                  totalPossibleScore={project.totalPossibleScore}
                  score={project.mySubmission?.score}
                  isCourseInsightsEnabled={isCourseInsightsEnabled}
                />
              </For>

              <If condition={!nextProject}>
                <p className='font-semibold text-lg'>You've completed this pathway 🥳</p>
              </If>

              <If condition={isAssignmentClosed}>
                <p className='font-semibold text-lg text-orange-500'>Submissions are closed for this pathway ⌛</p>
              </If>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

export default CourseDetails
